/**
 * MileageActionModal — Extracted from ImmeublePortal.jsx (Batch #12 refactor).
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "@/lib/toast";
import { ChevronDown, Plus, MapPin, Pencil, Loader2, Trash2, BookOpen, Star } from "lucide-react";
import { API, BaseActionModal, EmptyHint, FieldLabel, authH } from "./shared";

export function MileageActionModal({ buildings, onClose, onReload }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    trip_date: today,
    from_location: "Domicile",
    to_location: buildings[0]?.address || "",
    km_distance: "",
    purpose: "Visite immeuble",
    vehicle: "",
    notes: "",
  });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [calcBusy, setCalcBusy] = useState(false);
  const [mapsConfigured, setMapsConfigured] = useState(false);
  const [autoResult, setAutoResult] = useState(null);
  // Banque d'adresses : refonte du module
  const [addressBook, setAddressBook] = useState([]);
  const [fromMode, setFromMode] = useState("book"); // 'book' | 'manual'
  const [toMode, setToMode] = useState("book");
  // Gestion de la banque (CRUD)
  const [showAddressManager, setShowAddressManager] = useState(false);
  const [newAddr, setNewAddr] = useState({
    label: "", civic_number: "", street: "", city: "", postal_code: "", category: "other",
  });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchItems = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/portal/immeuble/mileage`, { headers: authH() });
      setItems(r.data?.items || []);
      setTotal(r.data?.total_km || 0);
    } catch (_e) {
      // permission denied OK, silencieux
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchItems();
    // Check Google Maps availability
    axios.get(`${API}/portal/immeuble/mileage/maps-status`, { headers: authH() })
      .then((r) => setMapsConfigured(!!r.data?.configured))
      .catch(() => setMapsConfigured(false));
    // Charger la banque d'adresses
    axios.get(`${API}/portal/immeuble/address-book`, { headers: authH() })
      .then((r) => setAddressBook(r.data?.items || []))
      .catch(() => setAddressBook([]));
  }, []);

  const reloadAddressBook = async () => {
    try {
      const r = await axios.get(`${API}/portal/immeuble/address-book`, { headers: authH() });
      setAddressBook(r.data?.items || []);
    } catch (_e) { /* silent */ }
  };

  const saveCurrentAsAddress = async (which) => {
    const address = which === "from" ? form.from_location : form.to_location;
    if (!address?.trim()) { toast.error("Saisir une adresse d'abord"); return; }
    try {
      await axios.post(`${API}/portal/immeuble/address-book`, { address, label: address.slice(0, 60) }, { headers: authH() });
      toast.success("Adresse ajoutée à la banque");
      reloadAddressBook();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const createAddress = async () => {
    if (!newAddr.street.trim()) return toast.error("Rue requise");
    if (!newAddr.label.trim()) return toast.error("Libellé requis");
    try {
      await axios.post(`${API}/portal/immeuble/address-book`, newAddr, { headers: authH() });
      toast.success("Adresse enregistrée");
      setNewAddr({ label: "", civic_number: "", street: "", city: "", postal_code: "", category: "other" });
      reloadAddressBook();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteAddress = async (id) => {
    if (!window.confirm("Supprimer cette adresse de votre banque ?")) return;
    try {
      await axios.delete(`${API}/portal/immeuble/address-book/${id}`, { headers: authH() });
      toast.success("Adresse supprimée");
      reloadAddressBook();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const calculateAuto = async () => {
    if (!form.from_location || !form.to_location) {
      toast.error("Renseignez l'origine et la destination");
      return;
    }
    setCalcBusy(true);
    setAutoResult(null);
    try {
      const r = await axios.post(`${API}/portal/immeuble/mileage/calculate-distance`, {
        origin: form.from_location,
        destination: form.to_location,
      }, { headers: authH() });
      upd("km_distance", String(r.data.distance_km));
      setAutoResult({
        km: r.data.distance_km,
        duration: r.data.duration_min,
        origin: r.data.origin_resolved,
        destination: r.data.destination_resolved,
      });
      toast.success(`${r.data.distance_km} km · ~${r.data.duration_min} min`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur Google Maps");
    } finally {
      setCalcBusy(false);
    }
  };

  const submit = async () => {
    if (!form.from_location || !form.to_location) { toast.error("Origine et destination requises"); return; }
    if (!form.km_distance || Number(form.km_distance) <= 0) { toast.error("Distance invalide"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/portal/immeuble/mileage`, {
        ...form,
        km_distance: Number(form.km_distance),
      }, { headers: authH() });
      toast.success(`Trajet de ${form.km_distance} km enregistré`);
      setForm({ ...form, km_distance: "", notes: "" });
      fetchItems();
      onReload?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <BaseActionModal
      title="Suivi kilométrage"
      subtitle="Domicile ↔ Immeuble · Déductible fiscalement"
      tone="rose"
      icon={MapPin}
      testId="immeuble-action-modal-mileage"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Fermer</button>
          <button onClick={submit} disabled={busy} className="px-4 py-1.5 text-sm rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="immeuble-mileage-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Enregistrer
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <FieldLabel label="Date du trajet" required>
            <input type="date" value={form.trip_date} onChange={(e) => upd("trip_date", e.target.value)} className="cc-input" />
          </FieldLabel>
          <FieldLabel label="Distance (km)" required>
            <input type="number" step="0.1" min="0" value={form.km_distance} onChange={(e) => upd("km_distance", e.target.value)} className="cc-input font-mono" data-testid="immeuble-mileage-km" />
          </FieldLabel>
        </div>

        {/* Bouton « Gérer mes adresses » + manager intégré */}
        <div className="border border-dashed border-slate-300 rounded-lg p-2.5 bg-slate-50">
          <button
            type="button"
            onClick={() => setShowAddressManager(!showAddressManager)}
            className="w-full text-left flex items-center justify-between text-xs font-semibold text-slate-700 hover:text-slate-900"
            data-testid="immeuble-address-manager-toggle"
          >
            <span className="inline-flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5 text-rose-600" />
              Gérer ma banque d'adresses ({addressBook.length})
            </span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAddressManager ? "rotate-180" : ""}`} />
          </button>
          {showAddressManager && (
            <div className="mt-3 space-y-2.5" data-testid="immeuble-address-manager">
              {/* Form Nouvelle adresse — 5 champs structurés (Batch #4) */}
              <div className="bg-white rounded-lg p-2.5 border border-slate-200 space-y-1.5">
                <span className="text-[10px] font-semibold text-slate-600 uppercase block">Nouvelle adresse — 5 champs pour précision Google Maps</span>
                <div className="grid grid-cols-12 gap-1.5">
                  <input
                    value={newAddr.civic_number}
                    onChange={(e) => setNewAddr({ ...newAddr, civic_number: e.target.value })}
                    placeholder="N° civique"
                    className="col-span-2 px-2 py-1 text-xs border rounded"
                    data-testid="immeuble-address-new-civic"
                  />
                  <input
                    value={newAddr.street}
                    onChange={(e) => setNewAddr({ ...newAddr, street: e.target.value })}
                    placeholder="Rue (ex: J-R Théberge)"
                    className="col-span-6 px-2 py-1 text-xs border rounded"
                    data-testid="immeuble-address-new-street"
                  />
                  <input
                    value={newAddr.city}
                    onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })}
                    placeholder="Ville"
                    className="col-span-3 px-2 py-1 text-xs border rounded"
                    data-testid="immeuble-address-new-city"
                  />
                  <select
                    value={newAddr.category}
                    onChange={(e) => setNewAddr({ ...newAddr, category: e.target.value })}
                    className="col-span-1 px-1 py-1 text-xs border rounded"
                    title="Catégorie"
                  >
                    <option value="home">🏠</option>
                    <option value="office">🏢</option>
                    <option value="rental">🏘️</option>
                    <option value="supplier">🔧</option>
                    <option value="other">📍</option>
                  </select>
                </div>
                <div className="grid grid-cols-12 gap-1.5">
                  <input
                    value={newAddr.postal_code}
                    onChange={(e) => setNewAddr({ ...newAddr, postal_code: e.target.value.toUpperCase() })}
                    placeholder="Code postal (J2S 1A4)"
                    className="col-span-4 px-2 py-1 text-xs border rounded font-mono"
                    data-testid="immeuble-address-new-postal"
                  />
                  <input
                    value={newAddr.label}
                    onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })}
                    placeholder="Libellé (ex: Domicile, Immeuble A)"
                    className="col-span-7 px-2 py-1 text-xs border rounded"
                    data-testid="immeuble-address-new-label"
                  />
                  <button
                    type="button"
                    onClick={createAddress}
                    disabled={!newAddr.street.trim() || !newAddr.label.trim()}
                    className="col-span-1 py-1 rounded bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40 inline-flex items-center justify-center"
                    data-testid="immeuble-address-new-submit"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              {/* Liste adresses existantes */}
              {addressBook.length === 0 ? (
                <p className="text-[11px] text-slate-400 italic text-center py-2">Aucune adresse enregistrée.</p>
              ) : (
                <ul className="space-y-1 max-h-[180px] overflow-y-auto" data-testid="immeuble-address-list">
                  {addressBook.map((a) => (
                    <li key={a.id} className="flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1.5 text-xs">
                      <Star className={`w-3 h-3 flex-shrink-0 ${a.usage_count > 5 ? "text-amber-500" : "text-slate-300"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-800 truncate">{a.label}</div>
                        <div className="text-[10px] text-slate-500 truncate">{a.address}</div>
                      </div>
                      <span className="text-[9px] text-slate-400 whitespace-nowrap">{a.usage_count}×</span>
                      <button
                        type="button"
                        onClick={() => deleteAddress(a.id)}
                        className="p-0.5 text-rose-500 hover:bg-rose-50 rounded"
                        data-testid={`immeuble-address-delete-${a.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <FieldLabel label="Origine" required>
          <div className="space-y-1.5">
            <div className="flex gap-1 text-[10px]">
              <button type="button" onClick={() => setFromMode("book")}
                      className={`px-2 py-0.5 rounded ${fromMode === "book" ? "bg-rose-100 text-rose-700 font-semibold" : "text-slate-500 hover:bg-slate-50"}`}
                      data-testid="immeuble-mileage-from-mode-book">
                <BookOpen className="w-2.5 h-2.5 inline mr-0.5" /> Banque
              </button>
              <button type="button" onClick={() => setFromMode("manual")}
                      className={`px-2 py-0.5 rounded ${fromMode === "manual" ? "bg-rose-100 text-rose-700 font-semibold" : "text-slate-500 hover:bg-slate-50"}`}
                      data-testid="immeuble-mileage-from-mode-manual">
                <Pencil className="w-2.5 h-2.5 inline mr-0.5" /> Manuel
              </button>
              {fromMode === "manual" && form.from_location && (
                <button type="button" onClick={() => saveCurrentAsAddress("from")}
                        className="ml-auto px-2 py-0.5 rounded text-[10px] text-emerald-600 hover:bg-emerald-50 inline-flex items-center gap-0.5"
                        data-testid="immeuble-mileage-from-save">
                  <Plus className="w-2.5 h-2.5" /> Ajouter à la banque
                </button>
              )}
            </div>
            {fromMode === "book" ? (
              <select value={form.from_location} onChange={(e) => upd("from_location", e.target.value)} className="cc-input" data-testid="immeuble-mileage-from">
                <option value="">— Choisir une adresse —</option>
                {addressBook.map((a) => <option key={a.id} value={a.address}>{a.label} {a.usage_count > 0 ? `(${a.usage_count}×)` : ""}</option>)}
              </select>
            ) : (
              <input value={form.from_location} onChange={(e) => upd("from_location", e.target.value)} maxLength={120} className="cc-input" placeholder="Saisir une adresse manuellement" data-testid="immeuble-mileage-from" />
            )}
          </div>
        </FieldLabel>
        <FieldLabel label="Destination" required>
          <div className="space-y-1.5">
            <div className="flex gap-1 text-[10px]">
              <button type="button" onClick={() => setToMode("book")}
                      className={`px-2 py-0.5 rounded ${toMode === "book" ? "bg-rose-100 text-rose-700 font-semibold" : "text-slate-500 hover:bg-slate-50"}`}
                      data-testid="immeuble-mileage-to-mode-book">
                <BookOpen className="w-2.5 h-2.5 inline mr-0.5" /> Banque
              </button>
              <button type="button" onClick={() => setToMode("manual")}
                      className={`px-2 py-0.5 rounded ${toMode === "manual" ? "bg-rose-100 text-rose-700 font-semibold" : "text-slate-500 hover:bg-slate-50"}`}
                      data-testid="immeuble-mileage-to-mode-manual">
                <Pencil className="w-2.5 h-2.5 inline mr-0.5" /> Manuel
              </button>
              {toMode === "manual" && form.to_location && (
                <button type="button" onClick={() => saveCurrentAsAddress("to")}
                        className="ml-auto px-2 py-0.5 rounded text-[10px] text-emerald-600 hover:bg-emerald-50 inline-flex items-center gap-0.5"
                        data-testid="immeuble-mileage-to-save">
                  <Plus className="w-2.5 h-2.5" /> Ajouter à la banque
                </button>
              )}
            </div>
            {toMode === "book" ? (
              <select value={form.to_location} onChange={(e) => upd("to_location", e.target.value)} className="cc-input" data-testid="immeuble-mileage-to">
                <option value="">— Choisir une adresse —</option>
                <optgroup label="Mes immeubles">
                  {buildings.map((b) => (
                    <option key={b.id} value={b.address || b.name}>{b.name} · {b.address}</option>
                  ))}
                </optgroup>
                {addressBook.length > 0 && (
                  <optgroup label="Banque d'adresses">
                    {addressBook.map((a) => <option key={a.id} value={a.address}>{a.label} {a.usage_count > 0 ? `(${a.usage_count}×)` : ""}</option>)}
                  </optgroup>
                )}
              </select>
            ) : (
              <input value={form.to_location} onChange={(e) => upd("to_location", e.target.value)} maxLength={120} className="cc-input" placeholder="Saisir une adresse manuellement" data-testid="immeuble-mileage-to" />
            )}
          </div>
        </FieldLabel>

        {mapsConfigured && (
          <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-2.5">
            <button
              type="button"
              onClick={calculateAuto}
              disabled={calcBusy || !form.from_location || !form.to_location}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-rose-300 text-rose-700 text-xs font-semibold hover:bg-rose-100 disabled:opacity-40"
              data-testid="immeuble-mileage-calc-auto"
            >
              {calcBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
              Calculer automatiquement la distance (Google Maps)
            </button>
            {autoResult && (
              <div className="mt-2 text-[10px] text-slate-600 leading-snug" data-testid="immeuble-mileage-auto-result">
                <div className="font-mono text-rose-700 font-bold">
                  {autoResult.km} km · {autoResult.duration} min
                </div>
                <div className="truncate">
                  <span className="opacity-60">De :</span> {autoResult.origin}
                </div>
                <div className="truncate">
                  <span className="opacity-60">À :</span> {autoResult.destination}
                </div>
              </div>
            )}
          </div>
        )}
        {!mapsConfigured && (
          <div className="text-[10px] text-slate-400 italic px-1">
            💡 Astuce : pour le calcul automatique de la distance, contactez votre administrateur pour activer Google Maps.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <FieldLabel label="Motif">
            <input value={form.purpose} onChange={(e) => upd("purpose", e.target.value)} maxLength={120} className="cc-input" />
          </FieldLabel>
          <FieldLabel label="Véhicule">
            <input value={form.vehicle} onChange={(e) => upd("vehicle", e.target.value)} maxLength={80} placeholder="ex: Honda Civic 2020" className="cc-input" />
          </FieldLabel>
        </div>
        <FieldLabel label="Notes">
          <textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="cc-input" />
        </FieldLabel>

        <div className="border-t pt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Historique <span className="font-normal text-slate-400">({items.length})</span>
            </div>
            <div className="text-[11px] font-mono font-bold text-rose-700">
              Total : {total.toLocaleString("fr-CA")} km
            </div>
          </div>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto my-3" />
          ) : items.length === 0 ? (
            <EmptyHint icon={MapPin} message="Aucun trajet enregistré." />
          ) : (
            <div className="space-y-1 max-h-[180px] overflow-y-auto">
              {items.slice(0, 10).map((i) => (
                <div key={i.id} className="text-[11px] flex items-start justify-between gap-2 p-1.5 rounded bg-slate-50">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-slate-700">{i.trip_date} · <b>{i.km_distance} km</b></div>
                    <div className="text-slate-500 truncate">{i.from_location} → {i.to_location} {i.purpose ? `· ${i.purpose}` : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </BaseActionModal>
  );
}
